import React, { useEffect, useRef, useState } from 'react';
import { AppButton, AppNumberField, AppSelectField, AppTextField } from '../../../../Components/FormKit';
import { fetchAseguradoras } from '../../../../services/adminRecotizacionApi';
import { MAX_ASEGURADORA, MAX_FOLIO, generarFolio, plazosDelSeguro, primaMensual } from '../../../../Utils/seguroManual';
import { seguroManualCardCSS } from './BudgetsManagement.css';
import { formatoMoneda } from './recotizarBudget';

const OTRA = '__otra__';
const OPCION_OTRA = { value: OTRA, label: 'Otra…' };

/* Seguro capturado a mano por el admin cuando RODAC no cotizó el auto (D-ADM-9).
 * Va en el `insuranceSlot` de CarDataForm, debajo de "Seguro".
 * Lo capturado vive en el diálogo (`valor`/`onChange`): si esta tarjeta se
 * desmonta (p. ej. al cambiar el plazo) vuelve con los mismos datos. */
const SeguroManualCard = ({ plazo, valor, onChange, errores, aplicado = false, pendiente = false, onAplicar, onQuitar }) => {
    const [opciones, setOpciones] = useState(null); // null = cargando
    const [modoOtra, setModoOtra] = useState(false);

    const company = valor?.company || '';
    const montos = valor?.montos || {};
    const folios = valor?.folios || {};
    const foliosAuto = valor?.foliosAuto || {};

    // Lo último capturado, para decidir "Otra…" cuando llega la lista.
    const companyRef = useRef(company);
    companyRef.current = company;

    // Pulsaron COTIZAR sin usar este seguro: se resalta y se trae a la vista.
    const resaltar = pendiente && !aplicado;
    const tarjetaRef = useRef(null);
    useEffect(() => {
        if (resaltar) tarjetaRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    }, [resaltar]);

    useEffect(() => {
        // La API comparte la petición entre montajes: si ya se desmontó, se ignora.
        let vivo = true;
        fetchAseguradoras()
            .then((lista) => {
                if (!vivo) return;
                const limpias = lista.filter((o) => o && o.value);
                const actual = companyRef.current;
                // Lo capturado antes con "Otra…" no viene en la lista: se abre el texto.
                if (limpias.length > 0 && actual && !limpias.some((o) => o.value === actual)) {
                    setModoOtra(true);
                }
                setOpciones(limpias);
            })
            .catch(() => {
                if (vivo) setOpciones([]);
            });
        return () => { vivo = false; };
    }, []);

    const conLista = Array.isArray(opciones) && opciones.length > 0;
    const enLista = conLista && opciones.some((o) => o.value === company);
    // Si la lista falló o vino vacía, solo hay texto libre.
    const soloTexto = opciones !== null && !conLista;
    const plazos = plazosDelSeguro(plazo);
    const errorGeneral = errores?.plazo || errores?.mezcla;

    const cambiar = (cambios) => onChange({ ...valor, ...cambios });
    // Escribirlo a mano lo vuelve folio de la aseguradora; "No lo tengo" genera uno.
    const escribirFolio = (p, texto) => cambiar({ folios: { ...folios, [p]: texto }, foliosAuto: { ...foliosAuto, [p]: false } });
    const sinFolio = (p) => cambiar({ folios: { ...folios, [p]: generarFolio() }, foliosAuto: { ...foliosAuto, [p]: true } });

    const elegirAseguradora = (e) => {
        if (e.value === OTRA) {
            setModoOtra(true);
            cambiar({ company: enLista ? '' : company });
            return;
        }
        setModoOtra(false);
        cambiar({ company: e.value || '' });
    };

    const campoTexto = (label) => (
        <AppTextField
            label={label}
            value={company}
            onChange={(e) => cambiar({ company: e.target.value })}
            placeholder="Ej. Qualitas"
            maxLength={MAX_ASEGURADORA}
            required
            invalid={Boolean(errores?.company)}
            errorMessage={errores?.company}
        />
    );

    return (
        <section ref={tarjetaRef} className={`FormCard fk-root${resaltar ? ' smc--pendiente' : ''}`} css={seguroManualCardCSS()}>
            <div className="smc-header">
                <div>
                    <h2 className="smc-title">Seguro manual</h2>
                    <p className="smc-help">
                        RODAC no cotizó este auto. Si ya lo cotizaste con la aseguradora, captura la prima total con IVA de cada plazo.
                    </p>
                </div>
                <div className="smc-chips">
                    <span className="smc-chip"><i className="pi pi-lock" style={{ fontSize: '0.6rem' }} /> solo admin</span>
                    {aplicado && <span className="smc-chip smc-chip--ok"><i className="pi pi-check" style={{ fontSize: '0.6rem' }} /> en uso</span>}
                </div>
            </div>

            <div className="smc-grid">
                {soloTexto ? campoTexto('Aseguradora') : (
                    <AppSelectField
                        label="Aseguradora"
                        value={modoOtra ? OTRA : (enLista ? company : null)}
                        options={conLista ? [...opciones, OPCION_OTRA] : []}
                        optionLabel="label"
                        optionValue="value"
                        onChange={elegirAseguradora}
                        placeholder={opciones === null ? 'Cargando aseguradoras…' : 'Elige la aseguradora'}
                        disabled={opciones === null}
                        required
                        invalid={Boolean(errores?.company) && !modoOtra}
                        errorMessage={modoOtra ? undefined : errores?.company}
                    />
                )}
                {!soloTexto && modoOtra && campoTexto('Nombre de la aseguradora')}
            </div>

            {/* Cada plazo es su propia fila de seguro: prima y folio van juntos. */}
            <div className="smc-primas">
                {plazos.map((p) => {
                    const mensual = primaMensual(montos[p], p);
                    return (
                        <div key={p} className="smc-plazo">
                            <AppNumberField
                                label={`Prima ${p} meses`}
                                value={montos[p] ?? null}
                                onValueChange={(e) => cambiar({ montos: { ...montos, [p]: e.value } })}
                                mode="currency"
                                currency="MXN"
                                locale="es-MX"
                                minFractionDigits={2}
                                maxFractionDigits={2}
                                min={0}
                                placeholder="$0.00"
                                required
                                invalid={Boolean(errores?.montos?.[p])}
                                errorMessage={errores?.montos?.[p]}
                                helperText={mensual > 0 ? `≈ ${formatoMoneda(mensual, 0)}/mes` : undefined}
                            />
                            <AppTextField
                                label={`Folio ${p} meses`}
                                value={folios[p] || ''}
                                onChange={(e) => escribirFolio(p, e.target.value)}
                                maxLength={MAX_FOLIO}
                                required
                                invalid={Boolean(errores?.folios?.[p])}
                                errorMessage={errores?.folios?.[p]}
                                helperText={foliosAuto[p] ? 'Generado: no es de la aseguradora' : undefined}
                            />
                            <AppButton
                                label={foliosAuto[p] ? 'Generar otro' : 'No lo tengo'}
                                variant="ghost"
                                size="sm"
                                className="smc-sin-folio"
                                aria-label={foliosAuto[p] ? `Generar otro folio de ${p} meses` : `No tengo el folio de ${p} meses`}
                                onClick={() => sinFolio(p)}
                            />
                        </div>
                    );
                })}
            </div>

            {errorGeneral && <p className="smc-error">{errorGeneral}</p>}

            <div className="smc-acciones">
                {aplicado ? (
                    <>
                        <span className="smc-estado">
                            <i className="pi pi-check-circle" /> Se usará al pulsar COTIZAR
                        </span>
                        <AppButton label="Quitar seguro manual" icon="pi pi-times" variant="ghost" size="sm" onClick={onQuitar} />
                    </>
                ) : (
                    <>
                        <span className="smc-estado smc-estado--pendiente">
                            <i className="pi pi-arrow-right" /> Pulsa «Usar este seguro» y después COTIZAR
                        </span>
                        {/* Naranja: se distingue del verde de COTIZAR, que va después. */}
                        <AppButton label="Usar este seguro" icon="pi pi-check" size="sm" className="smc-usar" onClick={onAplicar} />
                    </>
                )}
            </div>
        </section>
    );
};

export default SeguroManualCard;
